<div class="bg-white dark:bg-slate-900 rounded-xl shadow dark:shadow-gray-700 p-6">
  <div class="flex justify-between items-center mb-6">
    <h2 class="text-2xl font-semibold text-slate-900 dark:text-white">Select Your Accommodation</h2>
    <div class="text-sm text-slate-600 dark:text-slate-400">
      <span class="font-medium">{{ totalRoomsSelected }}/{{ totalRoomsNeeded }}</span> rooms selected
    </div>
  </div>

  <!-- Room Selection Status -->
  <div class="mb-6 p-4 rounded-lg"
       [ngClass]="{
         'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800': canProceed(),
         'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800': !canProceed()
       }">
    <div class="flex items-center">
      <i data-feather="info" class="w-5 h-5 mr-2"
         [ngClass]="{
           'text-green-600 dark:text-green-400': canProceed(),
           'text-yellow-600 dark:text-yellow-400': !canProceed()
         }"></i>
      <span class="text-sm font-medium"
            [ngClass]="{
              'text-green-800 dark:text-green-200': canProceed(),
              'text-yellow-800 dark:text-yellow-200': !canProceed()
            }">
        <span *ngIf="canProceed()">Perfect! You've selected all required rooms.</span>
        <span *ngIf="!canProceed()">Please select {{ totalRoomsNeeded - totalRoomsSelected }} more room(s) to continue.</span>
      </span>
    </div>
  </div>

  <!-- Available Rooms -->
  <div class="grid lg:grid-cols-1 gap-6 mb-8">
    <div *ngFor="let room of availableRooms"
         class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">

      <div class="md:flex">
        <!-- Room Image -->
        <div class="md:w-1/3">
          <div class="h-48 md:h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <i data-feather="image" class="w-12 h-12 text-gray-400"></i>
            <!-- Replace with actual image when available -->
            <!-- <img [src]="room.images[0]" [alt]="room.name" class="w-full h-full object-cover"> -->
          </div>
        </div>

        <!-- Room Details -->
        <div class="md:w-2/3 p-6">
          <div class="flex justify-between items-start mb-4">
            <div>
              <h3 class="text-xl font-semibold text-slate-900 dark:text-white mb-2">{{ room.name }}</h3>
              <p class="text-slate-600 dark:text-slate-400 mb-3">{{ room.description }}</p>

              <!-- Room Features -->
              <div class="flex flex-wrap gap-2 mb-4">
                <span *ngFor="let feature of room.features"
                      class="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                  {{ feature }}
                </span>
              </div>

              <!-- Room Info -->
              <div class="flex items-center text-sm text-slate-600 dark:text-slate-400 mb-4">
                <i data-feather="users" class="w-4 h-4 mr-1"></i>
                <span class="mr-4 flex items-center">
                  Max {{ room.maxOccupancy }} guests
                  <span *ngIf="getRoomCapacityStatus(room) === 'suitable'"
                        class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <i data-feather="check" class="w-3 h-3 mr-1"></i>
                    Perfect fit
                  </span>
                  <span *ngIf="getRoomCapacityStatus(room) === 'adequate'"
                        class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                    <i data-feather="alert-triangle" class="w-3 h-3 mr-1"></i>
                    Adequate
                  </span>
                  <span *ngIf="getRoomCapacityStatus(room) === 'insufficient'"
                        class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    <i data-feather="x" class="w-3 h-3 mr-1"></i>
                    Too small
                  </span>
                </span>
                <i data-feather="calendar" class="w-4 h-4 mr-1 ml-4"></i>
                <span>{{ nights }} night{{ nights > 1 ? 's' : '' }}</span>
              </div>
            </div>

            <!-- Price and Selection -->
            <div class="text-right">
              <div class="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                ${{ room.pricePerNight }}
              </div>
              <div class="text-sm text-slate-600 dark:text-slate-400 mb-4">per night</div>

              <!-- Room Selection Controls -->
              <div *ngIf="!isRoomSelected(room.id)" class="space-y-2">
                <button
                  (click)="selectRoom(room)"
                  [disabled]="totalRoomsSelected >= totalRoomsNeeded"
                  class="w-full py-2 px-4 text-sm bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                  Select Room
                </button>
              </div>

              <div *ngIf="isRoomSelected(room.id)" class="space-y-2">
                <div class="flex items-center justify-center gap-2 mb-2">
                  <button
                    (click)="adjustRoomQuantity(room.id, -1)"
                    class="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <i data-feather="minus" class="w-3 h-3"></i>
                  </button>
                  <span class="w-8 text-center font-medium text-slate-900 dark:text-white">
                    {{ getRoomQuantity(room.id) }}
                  </span>
                  <button
                    (click)="adjustRoomQuantity(room.id, 1)"
                    [disabled]="totalRoomsSelected >= totalRoomsNeeded"
                    class="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    <i data-feather="plus" class="w-3 h-3"></i>
                  </button>
                </div>
                <div class="text-sm text-green-600 dark:text-green-400 font-medium">
                  Total: ${{ room.pricePerNight * getRoomQuantity(room.id) * nights }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Selected Rooms Summary -->
  <div *ngIf="selectedRooms.length > 0" class="mb-8">
    <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-4">Selected Rooms</h3>
    <div class="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
      <div *ngFor="let room of selectedRooms" class="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
        <div>
          <span class="font-medium text-slate-900 dark:text-white">{{ room.roomName }}</span>
          <span class="text-sm text-slate-600 dark:text-slate-400 ml-2">× {{ room.quantity }}</span>
        </div>
        <div class="text-right">
          <div class="font-medium text-slate-900 dark:text-white">${{ getRoomTotal(room) }}</div>
          <div class="text-sm text-slate-600 dark:text-slate-400">{{ nights }} nights</div>
        </div>
      </div>

      <div class="flex justify-between items-center pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        <span class="text-lg font-semibold text-slate-900 dark:text-white">Total Accommodation</span>
        <span class="text-xl font-bold text-red-500">${{ getTotalCost() }}</span>
      </div>
    </div>
  </div>

  <!-- Capacity Warning -->
  <div *ngIf="!hasAdequateCapacity() && selectedRooms.length > 0"
       class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
    <div class="flex items-start">
      <i data-feather="alert-triangle" class="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0"></i>
      <div>
        <h3 class="text-sm font-medium text-red-900 dark:text-red-200 mb-1">Insufficient Room Capacity</h3>
        <p class="text-sm text-red-700 dark:text-red-300">{{ getCapacityMessage() }}</p>
      </div>
    </div>
  </div>

  <!-- Action Buttons -->
  <div class="flex justify-between pt-4">
    <button
      type="button"
      (click)="onPrevious()"
      class="py-2 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-gray-500 hover:bg-gray-600 text-white rounded-md cursor-pointer">
      <i data-feather="arrow-left" class="w-4 h-4 mr-2 inline"></i>
      Previous
    </button>

    <button
      type="button"
      (click)="onNext()"
      [disabled]="!canProceed()"
      class="py-2 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-red-500 hover:bg-red-600 text-white rounded-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
      Continue to Services
      <i data-feather="arrow-right" class="w-4 h-4 ml-2 inline"></i>
    </button>
  </div>
</div>
